import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import './ChatInterface.css';

const WS_URL    = process.env.REACT_APP_WS_URL || 'ws://localhost:3001';
const POOL_ID   = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID    || '';

/* ── Auth helpers ───────────────────────────────────────────────── */
function getIdToken() {
  return new Promise((resolve) => {
    if (!POOL_ID || !CLIENT_ID) { resolve(null); return; }
    const pool = new CognitoUserPool({ UserPoolId: POOL_ID, ClientId: CLIENT_ID });
    const user = pool.getCurrentUser();
    if (!user) { resolve(null); return; }
    user.getSession((err, session) => {
      resolve(err || !session?.isValid() ? null : session.getIdToken().getJwtToken());
    });
  });
}

function getUserIdentity() {
  return new Promise((resolve) => {
    if (!POOL_ID || !CLIENT_ID) { resolve({ email: null, name: 'User' }); return; }
    const pool = new CognitoUserPool({ UserPoolId: POOL_ID, ClientId: CLIENT_ID });
    const user = pool.getCurrentUser();
    if (!user) { resolve({ email: null, name: 'User' }); return; }
    user.getSession((err, session) => {
      if (err || !session?.isValid()) { resolve({ email: null, name: 'User' }); return; }
      try {
        const b64 = session.getIdToken().getJwtToken().split('.')[1];
        const pad = b64.length % 4 === 0 ? 0 : 4 - (b64.length % 4);
        const p = JSON.parse(atob(b64 + '='.repeat(pad)));
        resolve({
          email: p.email || p['cognito:username'] || null,
          name: p.name || p.email || p['cognito:username'] || 'User',
        });
      } catch {
        resolve({ email: null, name: 'User' });
      }
    });
  });
}

/* ── Session storage helpers ────────────────────────────────────── */
const sessionsKey = (email) => `neb_sessions_${email || 'anon'}`;

function loadSessions(email) {
  try { return JSON.parse(localStorage.getItem(sessionsKey(email)) || '[]'); }
  catch { return []; }
}

function persistSessions(email, sessions) {
  try { localStorage.setItem(sessionsKey(email), JSON.stringify(sessions.slice(0, 60))); }
  catch {}
}

/* ── Static content ─────────────────────────────────────────────── */
const WELCOME = {
  underwriter: {
    role: 'assistant',
    content:
      "Welcome to the **Nebulane Risk Desk Decision Workspace**.\n\n" +
      "I can help you review underwriting risk, summarize medical history, investigate suspicious claims, " +
      "and translate portfolio signals into operational next steps.\n\n" +
      "Choose a starter workflow below or ask a direct question about an applicant, claim, or underwriting scenario.",
  },
  applicant: {
    role: 'assistant',
    content:
      "Welcome to **Nebulane Insurance Support**.\n\n" +
      "I'm here to help you with your insurance application — whether you have questions about coverage, " +
      "need to know what's next in the process, or need help with your documentation.\n\n" +
      "Choose a topic below or type your question.",
  },
};

const QUICK_PROMPTS = {
  underwriter: [
    { title: 'Risk profile review',  detail: 'Run a structured triage on a submitted application.', text: 'Assess risk for APP-0900' },
    { title: 'Claim investigation',  detail: 'Look for fraud signals or inconsistencies in a claim.',  text: 'Investigate claim CLM-0492' },
    { title: 'Portfolio snapshot',   detail: 'Summarize underwriting patterns across the current queue.', text: 'Give me a portfolio analytics summary' },
    { title: 'Platform health',      detail: 'Confirm systems and decision tools are operating normally.', text: 'Run a system health check' },
  ],
  applicant: [
    { title: 'Application status',  detail: 'Find out where your application is in the review process.',  text: 'What happens after I submit my application?' },
    { title: 'Coverage questions',  detail: 'Understand what your policy covers and what to expect.',     text: 'Can you explain what my insurance policy covers?' },
    { title: 'Documentation help',  detail: 'Get guidance on what documents you need to provide.',        text: 'What documents do I need to submit for my application?' },
    { title: 'How it works',        detail: 'Learn about the application and approval process.',           text: 'Walk me through the insurance application process from start to finish.' },
  ],
};

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(ts) {
  const d = new Date(ts);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function sessionTitle(messages) {
  const first = messages.find(m => m.role === 'user');
  if (!first) return 'New conversation';
  const t = first.content || '';
  return t.length > 48 ? t.slice(0, 48) + '…' : t;
}

/* ── Component ──────────────────────────────────────────────────── */
function ChatInterface({ pendingMessage, onPendingMessageConsumed, userRole = 'applicant', contextApplicantId = null }) {
  const role = userRole === 'underwriter' ? 'underwriter' : 'applicant';

  const welcomeMsg = useCallback(() => ({ ...WELCOME[role], timestamp: Date.now() }), [role]);

  const [messages, setMessages]           = useState(() => [welcomeMsg()]);
  const [input, setInput]                 = useState('');
  const [isLoading, setIsLoading]         = useState(false);
  const [streamingContent, setStreaming]  = useState('');
  const [statusText, setStatusText]       = useState('');
  const [connStatus, setConnStatus]       = useState('idle');
  const [sidebarOpen, setSidebarOpen]     = useState(true);
  const [sessions, setSessions]           = useState([]);
  const [viewSession, setViewSession]     = useState(null);
  const [userEmail, setUserEmail]         = useState(null);
  const [userName, setUserName]           = useState('User');

  const messagesEndRef  = useRef(null);
  const inputRef        = useRef(null);
  const wsRef           = useRef(null);
  const accumulatedRef  = useRef('');
  const resolveRef      = useRef(null);
  const rejectRef       = useRef(null);
  const charQueueRef    = useRef([]);
  const animFrameRef    = useRef(null);
  const streamDisplayRef = useRef('');
  const messagesRef     = useRef(messages);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  /* Load user identity & sessions once on mount */
  useEffect(() => {
    getUserIdentity().then(({ email, name }) => {
      setUserEmail(email);
      setUserName(name);
      setSessions(loadSessions(email));
    });
  }, []);

  /* Save sessions to localStorage whenever they change */
  useEffect(() => {
    if (userEmail !== null) persistSessions(userEmail, sessions);
  }, [sessions, userEmail]);

  /* Save current session before navigating away */
  useEffect(() => {
    return () => {
      const msgs = messagesRef.current;
      if (msgs.length > 1) commitSession(msgs);
    };
  }, []); // eslint-disable-line

  function commitSession(msgs) {
    const userMsgs = msgs.filter(m => m.role === 'user');
    if (userMsgs.length === 0) return;
    const session = {
      id: Date.now().toString(),
      title: sessionTitle(msgs),
      timestamp: msgs[0].timestamp || Date.now(),
      role,
      messages: msgs,
    };
    setSessions(prev => {
      const next = [session, ...prev.filter(s => s.title !== session.title)];
      return next;
    });
  }

  /* ── Smooth character-by-character streaming ─────────────────── */
  const drainQueue = useCallback(() => {
    if (charQueueRef.current.length === 0) {
      animFrameRef.current = null;
      return;
    }
    const qLen = charQueueRef.current.length;
    const batch = qLen > 400 ? 24 : qLen > 150 ? 10 : qLen > 40 ? 4 : 2;
    const chars = charQueueRef.current.splice(0, batch).join('');
    streamDisplayRef.current += chars;
    setStreaming(streamDisplayRef.current);
    animFrameRef.current = requestAnimationFrame(drainQueue);
  }, []);

  /* ── WebSocket ───────────────────────────────────────────────── */
  const getWebSocket = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        resolve(wsRef.current); return;
      }
      if (wsRef.current) wsRef.current.close();
      setConnStatus('connecting');
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => { wsRef.current = ws; setConnStatus('connected'); resolve(ws); };
      ws.onerror = () => { setConnStatus('error'); reject(new Error('WebSocket connection failed')); };
      ws.onclose = () => { wsRef.current = null; setConnStatus('idle'); };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'chunk') {
            accumulatedRef.current += data.content;
            for (const ch of data.content) charQueueRef.current.push(ch);
            if (!animFrameRef.current) {
              animFrameRef.current = requestAnimationFrame(drainQueue);
            }
          } else if (data.type === 'status') {
            setStatusText(data.content);
          } else if (data.type === 'end') {
            if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
            if (charQueueRef.current.length > 0) {
              streamDisplayRef.current += charQueueRef.current.join('');
              charQueueRef.current = [];
              setStreaming(streamDisplayRef.current);
            }
            setStatusText('');
            if (resolveRef.current) {
              resolveRef.current(accumulatedRef.current || 'No response received.');
              resolveRef.current = null; rejectRef.current = null;
            }
          } else if (data.type === 'error') {
            setStatusText('');
            if (rejectRef.current) {
              rejectRef.current(new Error(data.content));
              resolveRef.current = null; rejectRef.current = null;
            }
          }
        } catch (e) { console.error('WS parse error:', e); }
      };
    });
  }, [drainQueue]);

  useEffect(() => () => { if (wsRef.current) wsRef.current.close(); }, []);

  useEffect(() => {
    if (pendingMessage && !isLoading) {
      onPendingMessageConsumed?.();
      dispatchMessage(pendingMessage);
    }
  }, [pendingMessage]); // eslint-disable-line

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, streamingContent, scrollToBottom]);

  /* ── Send & dispatch ─────────────────────────────────────────── */
  const sendMessage = async (userMessage) => {
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    accumulatedRef.current = '';
    streamDisplayRef.current = '';
    charQueueRef.current = [];

    const [ws, idToken] = await Promise.all([getWebSocket(), getIdToken()]);

    return new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current  = reject;
      ws.send(JSON.stringify({
        action:       'sendMessage',
        message:      userMessage,
        history,
        userRole:     role,
        idToken:      idToken || null,
        applicant_id: contextApplicantId || null,
      }));
    });
  };

  const dispatchMessage = async (userMessage) => {
    setInput('');
    setIsLoading(true);
    setStreaming('');
    setViewSession(null);
    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: Date.now() }]);

    try {
      const response = await sendMessage(userMessage);
      setMessages(prev => [...prev, { role: 'assistant', content: response, timestamp: Date.now() }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        timestamp: Date.now(),
      }]);
    } finally {
      setIsLoading(false);
      setStreaming('');
      inputRef.current?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || isLoading) return;
    await dispatchMessage(msg);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
  };

  const clearChat = () => {
    commitSession(messages);
    setMessages([welcomeMsg()]);
    setStreaming('');
    setStatusText('');
    setViewSession(null);
  };

  const openSession = (sess) => setViewSession(sess);
  const closeSession = () => setViewSession(null);

  const continueFromSession = (sess) => {
    if (messages.length > 1) commitSession(messages);
    setMessages(sess.messages);
    setViewSession(null);
    setStreaming('');
    setStatusText('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const deleteSession = (e, id) => {
    e.stopPropagation();
    setSessions(prev => prev.filter(s => s.id !== id));
    if (viewSession?.id === id) setViewSession(null);
  };

  /* ── Derived ─────────────────────────────────────────────────── */
  const connDot = { idle: 'dot-idle', connecting: 'dot-connecting', connected: 'dot-connected', error: 'dot-error' }[connStatus];
  const showQuickPrompts = messages.length === 1 && !isLoading && !viewSession;
  const displayMessages = viewSession ? viewSession.messages : messages;

  const initials = userName
    ? userName.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase()
    : 'U';

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <div className="chat-shell">

      {/* ── Top header ─────────────────────────────────────────── */}
      <header className="chat-header">
        <div className="chat-header-left">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(o => !o)}
            title={sidebarOpen ? 'Hide history' : 'Show history'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          <div className="chat-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <div className="chat-header-text">
            <span className="chat-header-title">
              {viewSession
                ? viewSession.title.slice(0, 40) + (viewSession.title.length > 40 ? '…' : '')
                : role === 'underwriter' ? 'Risk Desk' : 'Insurance Support'}
            </span>
            <div className="chat-header-sub">
              {viewSession ? (
                <span className="history-badge">Viewing history · {formatDate(viewSession.timestamp)}</span>
              ) : (
                <>
                  <span className={`conn-dot ${connDot}`} />
                  {connStatus === 'connected' ? 'Online' : connStatus === 'connecting' ? 'Connecting…' : 'Ready'}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="chat-header-actions">
          <span className="chat-header-pill">
            {role === 'underwriter' ? 'AgentCore' : 'AI'}
          </span>

          {viewSession ? (
            <button className="clear-btn" onClick={closeSession}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="19" y1="5" x2="5" y2="19" /><line x1="5" y1="5" x2="19" y2="19" />
              </svg>
              <span>Back to chat</span>
            </button>
          ) : (
            <button className="clear-btn" onClick={clearChat} title="New session">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>New chat</span>
            </button>
          )}
        </div>
      </header>

      {/* ── Body: sidebar + main ────────────────────────────────── */}
      <div className="chat-body">

        {/* ── History sidebar ────────────────────────────────────── */}
        <aside className={`chat-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <span className="sidebar-title">History</span>
            <div className="sidebar-user">
              <span className="sidebar-avatar">{initials}</span>
              <span className="sidebar-name">{userName}</span>
            </div>
          </div>

          <button className="sidebar-new-btn" onClick={clearChat}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New conversation
          </button>

          <div className="sidebar-sessions">
            {sessions.length === 0 ? (
              <div className="sidebar-empty">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <p>No past conversations yet.<br />Start chatting to build history.</p>
              </div>
            ) : (
              sessions.map((sess) => (
                <button
                  key={sess.id}
                  className={`sidebar-session ${viewSession?.id === sess.id ? 'active' : ''}`}
                  onClick={() => openSession(sess)}
                >
                  <div className="session-meta">
                    <span className="session-date">{formatDate(sess.timestamp)}</span>
                    <span className={`session-role-badge ${sess.role}`}>{sess.role === 'underwriter' ? 'Underwriter' : 'Applicant'}</span>
                  </div>
                  <span className="session-title">{sess.title}</span>
                  <div className="session-footer">
                    <span className="session-count">{sess.messages.filter(m => m.role === 'user').length} messages</span>
                    <div className="session-actions">
                      <button
                        className="session-continue"
                        onClick={(e) => { e.stopPropagation(); continueFromSession(sess); }}
                        title="Continue this conversation"
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                        Continue
                      </button>
                      <button
                        className="session-delete"
                        onClick={(e) => deleteSession(e, sess.id)}
                        title="Delete"
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* ── Chat main area ─────────────────────────────────────── */}
        <div className="chat-main">

          {/* History view banner */}
          {viewSession && (
            <div className="history-banner">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Viewing past conversation from {new Date(viewSession.timestamp).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
              <div className="history-banner-actions">
                <button onClick={() => continueFromSession(viewSession)} className="history-banner-continue">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                  Continue this conversation
                </button>
                <button onClick={closeSession} className="history-banner-close">Dismiss</button>
              </div>
            </div>
          )}

          <div className="messages-container">
            <div className="messages-inner">

              {displayMessages.map((msg, idx) => (
                <div key={idx} className={`message ${msg.role}`}>
                  <div className="message-avatar">
                    {msg.role === 'assistant' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                      </svg>
                    ) : (
                      <span className="user-initials">{initials}</span>
                    )}
                  </div>
                  <div className="message-body">
                    <div className="message-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                    {msg.timestamp && <span className="message-time">{formatTime(msg.timestamp)}</span>}
                  </div>
                </div>
              ))}

              {/* Quick prompts */}
              {showQuickPrompts && (
                <section className="quick-prompts-wrap">
                  <div className="quick-prompts-head">
                    <h2>{role === 'underwriter' ? 'Start with a common workflow' : 'How can I help you today?'}</h2>
                    <p>{role === 'underwriter'
                      ? 'Launch a structured prompt or type a custom instruction.'
                      : 'Pick a topic or type your question below.'}</p>
                  </div>
                  <div className="quick-prompts">
                    {QUICK_PROMPTS[role].map((p) => (
                      <button key={p.text} className="quick-card" onClick={() => !isLoading && dispatchMessage(p.text)}>
                        <span className="quick-card-title">{p.title}</span>
                        <span className="quick-card-detail">{p.detail}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Streaming message */}
              {isLoading && streamingContent && !viewSession && (
                <div className="message assistant">
                  <div className="message-avatar">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <div className="message-body">
                    <div className="message-content streaming">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingContent}</ReactMarkdown>
                      <span className="cursor" />
                    </div>
                  </div>
                </div>
              )}

              {/* Thinking indicator */}
              {isLoading && !streamingContent && !viewSession && (
                <div className="message assistant">
                  <div className="message-avatar pulse-avatar">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <div className="message-body">
                    <div className="message-content">
                      {statusText && (
                        <div className="status-pill">
                          <span className="status-spinner" />{statusText}
                        </div>
                      )}
                      <div className="typing-indicator">
                        <span /><span /><span />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          {!viewSession && (
            <form className="input-container" onSubmit={handleSubmit}>
              <div className="input-wrapper">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={role === 'underwriter'
                    ? 'Ask about an applicant, a risk profile, a medical issue, or a portfolio trend…'
                    : 'Ask about your application, coverage, documents, or the insurance process…'}
                  rows={1}
                  disabled={isLoading}
                />
                <button type="submit" disabled={!input.trim() || isLoading} className="send-btn">
                  {isLoading ? (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : (
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22,2 15,22 11,13 2,9" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="input-footer">
                <span className="input-hint">Enter to send · Shift+Enter for new line</span>
                <span className="powered-by">Powered by Amazon Bedrock AgentCore</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatInterface;
